// macadamia-app/src/macadamia-core/components/DossierSettingsModal.tsx
import React, { useState, useRef, useEffect } from 'react';
import { IDossier } from './widgets/types';
import XCircle from '../icons/XCircle';
import AuthorsSettings from './settings/AuthorsSettings';
import VisibilitySettings from './settings/VisibilitySettings';
import DeleteSettings from './settings/DeleteSettings';
import { useViewport } from './hooks/useViewport';

interface DossierSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dossier: IDossier;
  onSave: (updatedDossier: IDossier) => void;
  initialTab?: string;
  userRole: string | null;
}

const DossierSettingsModal: React.FC<DossierSettingsModalProps> = ({
  isOpen,
  onClose,
  dossier,
  onSave,
  initialTab,
  userRole
}) => {
  const [activeTab, setActiveTab] = useState(initialTab || 'Général');
  const [hoverCloseIcon, setHoverCloseIcon] = useState(false);
  const [visibility, setVisibility] = useState(dossier.settings.visibility);

  const [description, setDescription] = useState(dossier.description);
  const [resetParagraphNumbering, setResetParagraphNumbering] = useState(dossier.settings.resetParagraphNumbering || false);
  const [coAuthorsCanManageAccess, setCoAuthorsCanManageAccess] = useState(dossier.settings.coAuthorsCanManageAccess || false);
  const [allowDownload, setAllowDownload] = useState(dossier.settings.allowDownload ?? true);



  const modalRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useViewport();

  useEffect(() => {
    setActiveTab(initialTab || 'Général');
  }, [initialTab]);

  useEffect(() => {
    setResetParagraphNumbering(dossier.settings.resetParagraphNumbering || false);
    setCoAuthorsCanManageAccess(dossier.settings.coAuthorsCanManageAccess || false);
    setAllowDownload(dossier.settings.allowDownload ?? true);
    setVisibility(dossier.settings.visibility);
    setDescription(dossier.description);
  }, [dossier]);



  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleVisibilityChange = (value: string) => {
    setVisibility(value);
  };

  const handleSave = () => {
    const updatedDossier: IDossier = {
      ...dossier,
      settings: {
        ...dossier.settings,
        visibility,
        resetParagraphNumbering,
        coAuthorsCanManageAccess,
        allowDownload,
      },
      description,
    };
    onSave(updatedDossier);
    onClose();
  };

  const handleAddAuthor = (email: string) => {
    // Logique pour ajouter un auteur
  };

  const handleRemoveAuthor = (authorId: string) => {
    // Logique pour retirer un auteur
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'Général', label: 'Général' },
    { id: 'Visibilité', label: 'Visibilité' },
    { id: 'Auteurs', label: 'Auteurs' },
    { id: 'Mise en page', label: 'Mise en page' },
    { id: 'Suppression', label: 'Suppression', danger: true },
  ];

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-75 z-50 flex justify-center items-center ${isMobile ? 'px-4' : ''}`}>
      <div
        ref={modalRef}
        className={`bg-white dark:bg-mcdm-third-400 rounded-lg shadow-xl overflow-hidden flex ${isMobile
          ? 'flex-col w-full max-w-md h-[90vh]'
          : 'flex-row w-full max-w-5xl h-[700px] max-h-[90vh]'
          }`}
      >
        {/* Sidebar */}
        {/* Sidebar */}
        <div className={`${isMobile ? 'w-full h-auto border-b' : 'w-1/4 h-full border-r'} bg-mcdm-primary-200 dark:bg-mcdm-third-500 border-mcdm-primary-200 dark:border-mcdm-third-300 flex flex-col`}>
          {/* Sidebar Header */}
          <div className="p-4 pl-6">
            <h2 className="text-lg font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 truncate">
              {dossier.title}
            </h2>
            <div className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-200 mt-1">
              Paramètres
            </div>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 overflow-y-auto ${isMobile ? 'flex overflow-x-auto p-2 space-x-2' : 'flex-col p-2 space-y-1'}`}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                            group flex items-center px-4 py-2 text-sm rounded-md transition-all duration-200
                            ${isMobile ? 'whitespace-nowrap flex-shrink-0' : 'w-full text-left'}
                            ${activeTab === tab.id
                    ? 'bg-white dark:bg-mcdm-third-400 text-mcdm-third-500 dark:text-mcdm-primary-100 font-medium shadow-sm'
                    : 'text-mcdm-third-400 dark:text-mcdm-primary-200 hover:bg-black/5 dark:hover:bg-white/5 hover:text-mcdm-third-500 dark:hover:text-mcdm-primary-100'
                  }
                            ${tab.danger && activeTab !== tab.id ? 'text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300' : ''}
                        `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className={`${isMobile ? 'w-full flex-1' : 'w-3/4 h-full'} flex flex-col bg-white dark:bg-mcdm-third-400`}>
          {/* Header (Close Button) */}
          <div className="flex justify-end p-4 border-b border-mcdm-primary-100 dark:border-mcdm-third-300 shrink-0">
            <button
              onMouseEnter={() => setHoverCloseIcon(true)}
              onMouseLeave={() => setHoverCloseIcon(false)}
              onClick={onClose}
              className="transition-transform duration-200 hover:scale-105"
            >
              <XCircle fill={hoverCloseIcon ? '#ef4444' : '#9ca3af'} width="24" height="24" />
            </button>
          </div>

          {/* Main Content Body */}
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-3xl mx-auto">
              {activeTab === 'Général' && (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <h3 className="text-xl font-semibold mb-6 text-mcdm-third-500 dark:text-mcdm-primary-100">Général</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-mcdm-third-500 dark:text-mcdm-primary-200" htmlFor="description">
                        Description du dossier (Optionnel)
                      </label>
                    <textarea
                      id="description"
                      placeholder="Ajoutez une description courte pour identifier ce dossier..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full min-h-[150px] p-4 rounded-lg border border-mcdm-primary-200 dark:border-mcdm-third-200 bg-white dark:bg-mcdm-third-500 text-mcdm-third-500 dark:text-mcdm-primary-100 focus:ring-2 focus:ring-mcdm-secondary-400 focus:border-transparent outline-none transition-all resize-y"
                    />
                    <p className="mt-2 text-xs text-mcdm-third-300 dark:text-mcdm-primary-300">
                      Cette description est visible pour tous les membres ayant accès au dossier.
                    </p>
                  </div>

                    <div>
                      <h4 className="text-sm uppercase tracking-wider text-mcdm-third-300 dark:text-mcdm-primary-200 font-semibold mb-4 border-b border-mcdm-primary-200 dark:border-mcdm-third-300 pb-2">
                        Telechargement
                      </h4>
                      <label className="flex items-start cursor-pointer group">
                        <div className="relative flex items-center">
                          <input
                            type="checkbox"
                            checked={allowDownload}
                            onChange={(e) => setAllowDownload(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-mcdm-green-400/20 dark:peer-focus:ring-mcdm-green-400/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-mcdm-green-400"></div>
                        </div>
                        <div className="ml-3 text-sm">
                          <span className="font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 group-hover:text-mcdm-secondary-400 transition-colors">
                            Autoriser le telechargement
                          </span>
                          <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                            Permet aux utilisateurs ayant acces au dossier de le telecharger en PDF, Word ou Markdown.
                            Les auteurs et co-auteurs peuvent toujours telecharger.
                          </p>
                        </div>
                      </label>
                    </div>
                </div>
                </div>
              )}

            {activeTab === 'Visibilité' && (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                <h3 className="text-xl font-semibold mb-6 text-mcdm-third-500 dark:text-mcdm-primary-100">Visibilité et Accès</h3>
                <VisibilitySettings
                  visibility={visibility}
                  onVisibilityChange={handleVisibilityChange}
                  dossierId={dossier._id?.toString()}
                  coAuthorsCanManageAccess={coAuthorsCanManageAccess}
                  onCoAuthorsCanManageAccessChange={setCoAuthorsCanManageAccess}
                />
              </div>
            )}

            {activeTab === 'Auteurs' && (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                <h3 className="text-xl font-semibold mb-6 text-mcdm-third-500 dark:text-mcdm-primary-100">Auteurs et Collaborateurs</h3>
                <AuthorsSettings
                  dossier={dossier}
                  onAddAuthor={handleAddAuthor}
                  onRemoveAuthor={handleRemoveAuthor}
                />
              </div>
            )}

            {activeTab === 'Mise en page' && (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                <h3 className="text-xl font-semibold mb-2 text-mcdm-third-500 dark:text-mcdm-primary-100">Mise en page</h3>
                <p className="text-sm text-mcdm-third-400 dark:text-mcdm-primary-300 mb-8">Personnalisez l'apparence du contenu de ce dossier.</p>

                <div className="space-y-8">
                  {/* Section Listes removed */}

                  <section>
                    <h4 className="text-sm uppercase tracking-wider text-mcdm-third-300 dark:text-mcdm-primary-200 font-semibold mb-4 border-b border-mcdm-primary-200 dark:border-mcdm-third-300 pb-2">
                      Numérotation
                    </h4>
                    <label className="flex items-start cursor-pointer group">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          checked={resetParagraphNumbering}
                          onChange={(e) => setResetParagraphNumbering(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-mcdm-green-400/20 dark:peer-focus:ring-mcdm-green-400/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-mcdm-green-400"></div>
                      </div>
                      <div className="ml-3 text-sm">
                        <span className="font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 group-hover:text-mcdm-secondary-400 transition-colors">
                          Ré-initialiser la numérotation d'un chapitre à l'autre
                        </span>
                        <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                          Si activé, chaque nouveau chapitre recommencera le comptage des paragraphes à 1.
                          Sinon, la numérotation sera continue sur tout le dossier.
                        </p>
                      </div>
                    </label>
                  </section>

                  {/* Future sections placeholder */}
                  {/* 
                                <section>
                                    <h4 className="text-sm uppercase tracking-wider text-mcdm-third-300 dark:text-mcdm-primary-200 font-semibold mb-4 border-b border-mcdm-primary-200 dark:border-mcdm-third-300 pb-2">
                                        Police et Texte
                                    </h4>
                                    ...
                                </section> 
                                */}
                </div>
              </div>
            )}

            {activeTab === 'Suppression' && (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                <h3 className="text-xl font-semibold mb-6 text-red-500">Zone de danger</h3>
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg p-6">
                  <DeleteSettings
                    dossier={dossier}
                    onClose={onClose}
                    userRole={userRole}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        {activeTab !== 'Suppression' && (
          <div className="p-4 border-t border-mcdm-primary-100 dark:border-mcdm-third-300 flex justify-end gap-3 bg-gray-50/50 dark:bg-mcdm-third-400">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2.5 bg-mcdm-third-500 dark:bg-mcdm-primary-200 hover:bg-mcdm-third-600 dark:hover:bg-white text-white dark:text-mcdm-third-500 rounded-lg text-sm font-medium shadow-sm transition-all transform active:scale-95"
            >
              Enregistrer les modifications
            </button>
          </div>
        )}
      </div>
    </div>
    </div >
  );
};

export default DossierSettingsModal;